import React, { useCallback } from 'react';
import { useNewsFeed } from '../contexts/NewsFeedContext';
import { useLocale } from '../contexts/LocaleContext';
import { colors } from '../config/colors';
import toast from 'react-hot-toast';
import RiskIntelligenceFeed from '../components/RiskIntelligenceFeed/RiskIntelligenceFeed';

const News = () => {
  const { t } = useLocale();
  const {
    articles,
    loading,
    pagination,
    filters,
    setFilters,
    filterOptions,
    quickFilterCounts,
    trendingTopics,
    savedArticleIds,
    activeQuickFilter,
    setActiveQuickFilter,
    currentPage,
    setCurrentPage,
    companies,
    saveArticle,
    unsaveArticle,
    clearFilters,
    loadFeed,
    error,
  } = useNewsFeed();

  const handleSaveArticle = useCallback(
    async (article) => {
      const rawId = article?.id ?? article?.articleId ?? article?.article_id;
      const id = rawId != null ? String(rawId).trim() : '';
      if (!id) {
        toast.error(t('failedToSaveArticle'));
        return;
      }
      const isSaved = savedArticleIds.has(id);
      const res = isSaved ? await unsaveArticle(id) : await saveArticle(id);
      if (res?.success) {
        toast.success(isSaved ? t('articleRemovedFromSaved') : t('articleSaved'));
      } else {
        toast.error(res?.message || (isSaved ? t('failedToUnsaveArticle') : t('failedToSaveArticle')));
      }
    },
    [savedArticleIds, saveArticle, unsaveArticle, t]
  );

  const hasActiveFilters =
    filters.ticker ||
    filters.industry ||
    filters.dateFrom ||
    filters.dateTo ||
    (filters.q && filters.q.trim()) ||
    (filters.topic && filters.topic !== 'all') ||
    (filters.region && filters.region !== 'all') ||
    (filters.severity && filters.severity !== 'all');

  if (error) {
    toast.error(error);
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background.primary }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <RiskIntelligenceFeed
          articles={articles}
          loading={loading}
          showHeader={false}
          showSidebar={false}
          showSentimentQuickFilters={false}
          showSavedQuickFilter={false}
          applyFiltersImmediately={true}
          compact={false}
          search={filters.q}
          onSearchChange={(v) => setFilters({ q: v })}
          selectedTopic={filters.topic}
          onTopicChange={(v) => setFilters({ topic: v })}
          selectedRegion={filters.region}
          onRegionChange={(v) => setFilters({ region: v })}
          selectedSeverity={filters.severity}
          onSeverityChange={(v) => setFilters({ severity: v })}
          onFilterClick={loadFeed}
          onClearFilters={clearFilters}
          quickFilterCounts={quickFilterCounts}
          activeQuickFilter={activeQuickFilter}
          onQuickFilterChange={setActiveQuickFilter}
          filterOptions={filterOptions}
          pagination={pagination}
          onPageChange={setCurrentPage}
          onSaveArticle={handleSaveArticle}
          savedArticleIds={savedArticleIds}
          hasActiveFilters={!!hasActiveFilters}
          companies={companies}
          selectedTicker={filters.ticker}
          onTickerChange={(v) => setFilters({ ticker: v })}
          selectedIndustry={filters.industry}
          onIndustryChange={(v) => setFilters({ industry: v })}
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          onDateFromChange={(v) => setFilters({ dateFrom: v })}
          onDateToChange={(v) => setFilters({ dateTo: v })}
        />
      </div>
    </div>
  );
};

export default News;
